import React, { useEffect, useRef } from 'react';
import type { ReportSection, FormState } from '@types';
import AddressSelector from './AddressSelector';
import CountrySelect from './CountrySelect';

interface ReportSectionFormProps {
    section: ReportSection;
    onFormChange: (id: string, updatedFormData: Partial<FormState>) => void;
}

const ReportSectionForm: React.FC<ReportSectionFormProps> = ({ section, onFormChange }) => {
    const latestFormData = useRef(section.formData);
    useEffect(() => {
        latestFormData.current = section.formData;
    }, [section.formData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | { target: { name: string, value: any } }) => {
        const { name, value } = e.target;
        const updatedFormData = {
            ...latestFormData.current,
            [name]: value
        };
        latestFormData.current = updatedFormData;
        onFormChange(section.id, updatedFormData);
    };

    const renderFields = () => {
        switch (section.type) {
            case 'srm_facility_risk':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Primary Objective</label>
                            <textarea name="srm_objective" value={section.formData.srm_objective || ''} onChange={handleChange} rows={2} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                        <AddressSelector prefix="srm" formData={section.formData} onFormChange={handleChange} />
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-600 mb-1">Key Concerns</label>
                            <input type="text" name="srm_key_concerns" value={section.formData.srm_key_concerns || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                    </div>
                );
            case 'br_crisis_management':
                return (
                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Crisis Scenario Description</label>
                        <textarea name="br_crisis_description" value={section.formData.br_crisis_description || ''} onChange={handleChange} rows={3} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                    </div>
                );
            case 'trm_travel_brief':
                return (
                    <div className="space-y-4">
                        <input type="text" name="trm_traveler_name" value={section.formData.trm_traveler_name || ''} onChange={handleChange} placeholder="Traveler's Name" className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        <div className="grid grid-cols-2 gap-4">
                            <input type="date" name="trm_start_date" value={section.formData.trm_start_date || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                            <input type="date" name="trm_end_date" value={section.formData.trm_end_date || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                        <AddressSelector prefix="trm" formData={section.formData} onFormChange={handleChange} />
                    </div>
                );
            case 'ep_protective_intelligence':
                 return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Principal Name</label>
                            <input type="text" name="principal_name" value={section.formData.principal_name || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Threat Summary</label>
                            <textarea name="threat_summary" value={section.formData.threat_summary || ''} onChange={handleChange} rows={2} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                        <AddressSelector prefix="location" formData={section.formData} onFormChange={handleChange} />
                    </div>
                );
            case 'hsse_compliance_briefing':
                 return (
                     <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">State</label>
                            <input type="text" name="hsse_state" value={section.formData.hsse_state || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Operational Context</label>
                            <textarea name="hsse_context" value={section.formData.hsse_context || ''} onChange={handleChange} rows={2} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                     </div>
                );
            case 'gg_country_analysis':
            case 'crp_country_profile':
                const key = section.type === 'gg_country_analysis' ? 'gg_country' : 'crp_country';
                 return (
                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Country</label>
                        <CountrySelect name={key} value={section.formData[key] || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                    </div>
                 );
            case 'lms_training_module':
                 return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Training Topic</label>
                            <input type="text" name="lms_topic" value={section.formData.lms_topic || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Target Audience</label>
                            <input type="text" name="lms_audience" value={section.formData.lms_audience || ''} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Key Learning Objectives</label>
                            <textarea name="lms_objectives" value={section.formData.lms_objectives || ''} onChange={handleChange} rows={3} className="w-full bg-white border border-slate-300 rounded-md p-2" />
                        </div>
                    </div>
                );
            default:
                return <p className="text-sm text-slate-500">Configuration for this module is not yet available. It will be included as a general analysis based on the report title.</p>;
        }
    };

    return (
        <div>
            {renderFields()}
        </div>
    );
};

export default ReportSectionForm;
